import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import SelectList from "../../components/SelectList/SelectList";
import "../AdminDashboardPage/AdminDashboardPage.css";

const displayName = c => {
  const leaf = c.name.includes(':') ? c.name.split(':').pop().trim() : c.name;
  return c.acct_num ? `${c.acct_num} ${leaf}` : leaf;
};

const API = (process.env.REACT_APP_API_URL || "") + "/api/receipts";
const AUTH_API = (process.env.REACT_APP_API_URL || "") + "/api/auth";

export default function UserDashboardPage() {
  const [user, token, updatePreference] = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    location.state?.tab || "history"
  );

  // ── My Submissions tab state ──────────────────────────────────
  const [submissions, setSubmissions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  // ── View / Edit / Resubmit modal ──────────────────────────────
  const [viewSub, setViewSub] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLineItems, setEditLineItems] = useState([]);
  const [editFile, setEditFile] = useState(null);
  const [editRefData, setEditRefData] = useState({ vendors: [], accounts: [], categories: [], classes: [] });
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitMsg, setResubmitMsg] = useState("");

  // ── Settings tab state (TOTP) ─────────────────────────────────
  const [totpStatus, setTotpStatus] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpMsg, setTotpMsg] = useState("");

  // ── Settings tab state (Preferences) ──────────────────────────
  const [allCategories, setAllCategories] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [prefCategoryIds, setPrefCategoryIds] = useState(null);
  const [prefClassIds, setPrefClassIds] = useState(null);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMsg, setPrefMsg] = useState("");
  const [groupName, setGroupName] = useState(null);
  const [groupCategoryIds, setGroupCategoryIds] = useState([]);
  const [groupClassIds, setGroupClassIds] = useState([]);
  const [globalCategoryIds, setGlobalCategoryIds] = useState([]);
  const [globalClassIds, setGlobalClassIds] = useState([]);

  useEffect(() => {
    if (activeTab === "history") { loadSubmissions(); loadEditRefData(); }
    if (activeTab === "settings") { loadTotp(); loadPreferences(); }
  }, [activeTab]);

  // Show success banner if redirected back from /submit
  const successMsg = location.state?.submitted ? "Receipt submitted successfully!" : "";

  const loadSubmissions = () => {
    setSubsLoading(true);
    axios.get(`${API}/submissions/`, authHeader)
      .then(res => setSubmissions(res.data))
      .catch(console.error)
      .finally(() => setSubsLoading(false));
  };

  const loadEditRefData = () => {
    axios.get(`${API}/reference/`, authHeader)
      .then(res => setEditRefData({
        vendors: res.data.vendors || [],
        accounts: res.data.accounts || [],
        categories: res.data.categories || [],
        classes: res.data.classes || [],
      }))
      .catch(console.error);
  };

  const openViewModal = (sub) => {
    setViewSub(sub);
    setResubmitMsg("");
    setEditFile(null);
    setEditForm({
      vendor: sub.vendor || "",
      account: sub.account || "",
      date: sub.date || "",
      payment_type: sub.payment_type || "Debit",
      memo: sub.memo || "",
    });
    setEditLineItems(sub.line_items?.map(li => ({
      category: li.category || "",
      description: li.description || "",
      amount: li.amount || "",
      expense_class: li.expense_class || "",
    })) || []);
  };

  const updateEditLineItem = (i, field, value) => {
    setEditLineItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item));
  };

  const doResubmit = async () => {
    setResubmitting(true);
    setResubmitMsg("");
    try {
      const formData = new FormData();
      formData.append('vendor', editForm.vendor || "");
      formData.append('account', editForm.account || "");
      formData.append('date', editForm.date);
      formData.append('payment_type', editForm.payment_type);
      formData.append('memo', editForm.memo);
      formData.append('line_items', JSON.stringify(editLineItems));
      if (editFile) formData.append('receipt_file', editFile);
      const res = await axios.post(
        `${API}/submissions/${viewSub.id}/resubmit/`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setResubmitMsg(viewSub.status === 'pending' ? "Changes saved." : "Resubmitted! Waiting for admin review.");
      setSubmissions(prev => prev.map(s => s.id === res.data.id ? res.data : s));
      setTimeout(() => setViewSub(null), 1500);
    } catch (err) {
      setResubmitMsg(err.response?.data?.error || "Failed to resubmit.");
    } finally {
      setResubmitting(false);
    }
  };

  const loadTotp = () => {
    axios.get(`${AUTH_API}/totp-setup/`, authHeader)
      .then(res => setTotpStatus(res.data))
      .catch(console.error);
  };

  const loadPreferences = () => {
    axios.get(`${API}/user/preferences/`, authHeader)
      .then(res => {
        setAllCategories(res.data.all_categories || []);
        setAllClasses(res.data.all_classes || []);
        const groupCats = res.data.group_category_ids || [];
        const groupCls = res.data.group_class_ids || [];
        const globalCats = res.data.global_category_ids || [];
        const globalCls = res.data.global_class_ids || [];
        setPrefCategoryIds(
          res.data.category_ids.length > 0 ? res.data.category_ids
          : groupCats.length > 0 ? groupCats
          : globalCats.length > 0 ? globalCats
          : null
        );
        setPrefClassIds(
          res.data.class_ids.length > 0 ? res.data.class_ids
          : groupCls.length > 0 ? groupCls
          : globalCls.length > 0 ? globalCls
          : null
        );
        setGroupName(res.data.group_name || null);
        setGroupCategoryIds(res.data.group_category_ids || []);
        setGroupClassIds(res.data.group_class_ids || []);
        setGlobalCategoryIds(res.data.global_category_ids || []);
        setGlobalClassIds(res.data.global_class_ids || []);
      })
      .catch(err => {
        const msg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        setPrefMsg(`Failed to load preferences: ${msg}`);
      });
  };

  const resetToDefault = () => {
    const cat_ids = groupCategoryIds.length > 0 ? groupCategoryIds : globalCategoryIds;
    const class_ids = groupClassIds.length > 0 ? groupClassIds : globalClassIds;
    setPrefCategoryIds(cat_ids.length > 0 ? cat_ids : null);
    setPrefClassIds(class_ids.length > 0 ? class_ids : null);
    setPrefSaving(true);
    setPrefMsg("");
    axios.patch(`${API}/user/preferences/`, { category_ids: cat_ids, class_ids }, authHeader)
      .then(() => { setPrefMsg("Reset to defaults."); loadPreferences(); })
      .catch(() => setPrefMsg("Failed to reset."))
      .finally(() => setPrefSaving(false));
  };

  const savePreferences = () => {
    setPrefSaving(true);
    setPrefMsg("");
    const category_ids = prefCategoryIds ?? [];
    const class_ids = prefClassIds ?? [];
    axios.patch(`${API}/user/preferences/`, { category_ids, class_ids }, authHeader)
      .then(() => { setPrefMsg("Preferences saved."); loadPreferences(); })
      .catch(() => setPrefMsg("Failed to save."))
      .finally(() => setPrefSaving(false));
  };

  const togglePrefItem = (id, ids, setIds, allItems) => {
    if (ids === null) {
      setIds(allItems.map(i => i.id).filter(i => i !== id));
    } else if (ids.includes(id)) {
      const next = ids.filter(i => i !== id);
      setIds(next.length === 0 ? null : next);
    } else {
      const next = [...ids, id];
      setIds(next.length === allItems.length ? null : next);
    }
  };

  const setupTotp = () => {
    axios.get(`${AUTH_API}/totp-setup/`, authHeader)
      .then(res => setTotpStatus(res.data)).catch(console.error);
  };

  const confirmTotp = async () => {
    setTotpMsg("");
    try {
      await axios.post(`${AUTH_API}/totp-setup/`, { code: totpCode }, authHeader);
      setTotpMsg("2FA enabled successfully!");
      setTotpCode("");
      loadTotp();
    } catch (err) {
      setTotpMsg(err.response?.data?.error || "Invalid code.");
    }
  };

  const statusLabel = s => ({ pending: "Pending", needs_review: "Needs Review", submitted: "Matched", posted: "Posted", rejected: "Rejected" }[s] || s);
  const statusClass = s => ({ pending: "status-pending", needs_review: "status-needs-review", submitted: "status-submitted", posted: "status-posted", rejected: "status-rejected" }[s] || "");

  return (
    <div className="admin-page">
      <h1>My Dashboard</h1>

      <div className="tab-bar">
        <button
          className="tab-btn"
          onClick={() => navigate("/submit")}
        >
          + Submit Receipt
        </button>
        <button
          className="tab-btn"
          onClick={() => navigate("/missing-receipts")}
        >
          My Missing Receipts
        </button>
        {[["history", "My Submissions"], ["settings", "Settings"]].map(([id, label]) => (
          <button
            key={id}
            className={`tab-btn${activeTab === id ? " active" : ""}`}
            onClick={() => setActiveTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── My Submissions tab ── */}
      {activeTab === "history" && (
        <div className="tab-content">
          <div className="tab-header"><h2>My Submissions</h2></div>
          {successMsg && <div className="alert alert-success">{successMsg}</div>}
          {subsLoading && <p>Loading...</p>}
          {!subsLoading && submissions.length === 0 && (
            <p className="muted">No submissions yet.</p>
          )}
          {submissions.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Card</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(s => (
                  <tr key={s.id}>
                    <td>{s.date}</td>
                    <td>{s.vendor_name || <span className="muted">—</span>}</td>
                    <td>{s.card_last4 ? `•••• ${s.card_last4}` : (s.account_name || <span className="muted">—</span>)}</td>
                    <td>${parseFloat(s.total_amount || 0).toFixed(2)}</td>
                    <td><span className={`status-badge ${statusClass(s.status)}`}>{statusLabel(s.status)}</span></td>
                    <td>
                      <button className="btn-link" onClick={() => openViewModal(s)}>
                        {s.status === 'rejected' ? 'Edit & Resubmit' : (s.status === 'pending' || s.status === 'needs_review') ? 'Edit' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ── View / Edit / Resubmit modal ── */}
          {viewSub && (
            <div className="modal-overlay" onClick={() => setViewSub(null)}>
              <div className="modal-panel" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h2 style={{ margin: 0 }}>
                    {viewSub.status === 'rejected' ? 'Edit & Resubmit' : (viewSub.status === 'pending' || viewSub.status === 'needs_review') ? 'Edit Submission' : 'Submission Details'}
                  </h2>
                  <button className="btn-link" onClick={() => setViewSub(null)} style={{ fontSize: '1.2rem' }}>✕</button>
                </div>

                <p style={{ marginBottom: '1rem' }}>
                  Status: <span className={`status-badge ${statusClass(viewSub.status)}`}>{statusLabel(viewSub.status)}</span>
                </p>

                {(viewSub.status === 'rejected' || viewSub.status === 'pending' || viewSub.status === 'needs_review') ? (
                  <>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <label className="form-label">Date</label>
                        <input type="date" className="form-input" value={editForm.date}
                          onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                      </div>
                      <div style={{ flex: 2, minWidth: 200 }}>
                        <label className="form-label">Vendor</label>
                        <select className="form-select" value={editForm.vendor}
                          onChange={e => setEditForm(f => ({ ...f, vendor: e.target.value }))}>
                          <option value="">-- Select vendor --</option>
                          {editRefData.vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label className="form-label">Account</label>
                      <select className="form-select" value={editForm.account}
                        onChange={e => setEditForm(f => ({ ...f, account: e.target.value }))}>
                        <option value="">-- Select account --</option>
                        {editRefData.accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label className="form-label">Memo</label>
                      <textarea className="form-input" value={editForm.memo} rows={2}
                        onChange={e => setEditForm(f => ({ ...f, memo: e.target.value }))} />
                    </div>

                    <h3>Line Items</h3>
                    {editLineItems.map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem', alignItems: 'center' }}>
                        <select className="form-select" style={{ flex: 2, minWidth: 140 }} value={item.category}
                          onChange={e => updateEditLineItem(i, 'category', e.target.value)}>
                          <option value="">Category</option>
                          {editRefData.categories.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                        </select>
                        <input className="form-input" style={{ flex: 2, minWidth: 120 }} placeholder="Description"
                          value={item.description} onChange={e => updateEditLineItem(i, 'description', e.target.value)} />
                        <input className="form-input" type="number" style={{ flex: 1, minWidth: 80 }} placeholder="Amount"
                          value={item.amount} onChange={e => updateEditLineItem(i, 'amount', e.target.value)} />
                        <select className="form-select" style={{ flex: 1, minWidth: 100 }} value={item.expense_class}
                          onChange={e => updateEditLineItem(i, 'expense_class', e.target.value)}>
                          <option value="">Ministry</option>
                          {editRefData.classes.map(c => <option key={c.id} value={c.id}>{c.name.split(':').pop().trim()}</option>)}
                        </select>
                      </div>
                    ))}

                    <div style={{ marginBottom: '1rem', marginTop: '0.75rem' }}>
                      <label className="form-label">Replace Receipt (optional)</label>
                      <input type="file" accept="image/*,.pdf"
                        onChange={e => setEditFile(e.target.files[0])} />
                      {viewSub.attachments?.length > 0 && !editFile && (
                        <p className="muted" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                          Current: {viewSub.attachments[0].filename}
                        </p>
                      )}
                    </div>

                    {resubmitMsg && (
                      <p className={resubmitMsg.includes("Resubmitted") ? "msg-ok" : "msg-error"}
                        style={{ marginBottom: '0.75rem' }}>{resubmitMsg}</p>
                    )}
                    <div className="form-actions">
                      <button className="btn-primary" onClick={doResubmit} disabled={resubmitting}>
                        {resubmitting ? "Saving..." : viewSub.status === 'pending' ? "Save Changes" : "Resubmit"}
                      </button>
                      <button className="btn-secondary" onClick={() => setViewSub(null)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p><strong>Date:</strong> {viewSub.date}</p>
                    <p><strong>Vendor:</strong> {viewSub.vendor_name || <span className="muted">—</span>}</p>
                    <p><strong>Account:</strong> {viewSub.account_name || <span className="muted">—</span>}</p>
                    <p><strong>Amount:</strong> ${parseFloat(viewSub.total_amount || 0).toFixed(2)}</p>
                    {viewSub.memo && <p><strong>Memo:</strong> {viewSub.memo}</p>}
                    {viewSub.line_items?.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <strong>Line Items:</strong>
                        <table className="data-table" style={{ marginTop: '0.5rem' }}>
                          <thead><tr><th>Category</th><th>Description</th><th>Amount</th></tr></thead>
                          <tbody>
                            {viewSub.line_items.map((li, i) => (
                              <tr key={i}>
                                <td>{li.category_name || li.category}</td>
                                <td>{li.description}</td>
                                <td>${parseFloat(li.amount || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {viewSub.attachments?.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <strong>Receipt:</strong>{" "}
                        {viewSub.attachments.map(a => (
                          <a key={a.id} href={a.file_url} target="_blank" rel="noreferrer"
                            style={{ marginLeft: '0.5rem' }}>{a.filename}</a>
                        ))}
                      </div>
                    )}
                    <div className="form-actions" style={{ marginTop: '1.25rem' }}>
                      <button className="btn-secondary" onClick={() => setViewSub(null)}>Close</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Settings tab ── */}
      {activeTab === "settings" && (
        <div className="tab-content">
          <div className="tab-header"><h2>My Account</h2></div>
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3>Receipt Form Preferences</h3>
            <p className="muted" style={{ marginBottom: "0.5rem" }}>
              Choose which categories and classes appear in your receipt form.
              {groupName && <span> Your group default is <strong>{groupName}</strong>.</span>}
            </p>
            {prefMsg && <p className={prefMsg.includes("saved") ? "msg-ok" : "msg-error"} style={{ marginBottom: "0.5rem" }}>{prefMsg}</p>}
            {allCategories.length === 0 && !prefMsg && <p className="muted">Loading...</p>}
            {allCategories.length > 0 && (
              <>
                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <h4 style={{ marginBottom: "0.4rem" }}>Categories
                      <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                        onClick={() => setPrefCategoryIds(null)}>
                        Select All
                      </button>
                      <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }}
                        onClick={() => setPrefCategoryIds([])}>
                        Unselect All
                      </button>
                    </h4>
                    <SelectList
                      items={allCategories}
                      selectedIds={prefCategoryIds}
                      onToggle={id => togglePrefItem(id, prefCategoryIds, setPrefCategoryIds, allCategories)}
                      height="240px"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <h4 style={{ marginBottom: "0.4rem" }}>Ministries
                      <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                        onClick={() => setPrefClassIds(null)}>
                        Select All
                      </button>
                      <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }}
                        onClick={() => setPrefClassIds([])}>
                        Unselect All
                      </button>
                    </h4>
                    <SelectList
                      items={allClasses}
                      selectedIds={prefClassIds}
                      onToggle={id => togglePrefItem(id, prefClassIds, setPrefClassIds, allClasses)}
                      height="240px"
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <button className="btn-primary" onClick={savePreferences} disabled={prefSaving}>
                    {prefSaving ? "Saving..." : "Save"}
                  </button>
                  {(groupName || globalCategoryIds.length > 0) && (
                    <button className="btn-secondary" onClick={resetToDefault} disabled={prefSaving}>
                      Reset to Defaults
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3>Guided Walkthrough Prompt</h3>
            <p className="muted" style={{ marginBottom: "0.75rem" }}>
              When this is on, you'll see a "Walk me through it" prompt each time you open the receipt submission form.
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: "0.6rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={user?.show_submission_prompt ?? true}
                onChange={e => updatePreference('show_submission_prompt', e.target.checked)}
              />
              Show the guided walkthrough prompt on the submission form
            </label>
          </div>

          <div className="card">
            <h3>Two-Factor Authentication</h3>
            {!totpStatus && <button className="btn-secondary" onClick={setupTotp}>Load 2FA Settings</button>}
            {totpStatus && (
              <>
                <p>Status: <strong>{totpStatus.totp_enabled ? "Enabled" : "Not enabled"}</strong></p>
                {!totpStatus.totp_enabled && (
                  <>
                    <p className="muted">Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code to confirm.</p>
                    <img src={totpStatus.qr_code} alt="QR code" style={{ display: "block", margin: "1rem 0", width: 180, height: 180 }} />
                    <p className="muted" style={{ fontSize: "0.8rem" }}>Manual entry key: <code>{totpStatus.secret}</code></p>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.5rem" }}>
                      <input
                        type="text" placeholder="6-digit code" maxLength={6}
                        value={totpCode} onChange={e => setTotpCode(e.target.value)}
                        style={{ width: 130 }}
                      />
                      <button className="btn-primary" onClick={confirmTotp}>Confirm</button>
                    </div>
                    {totpMsg && <p className={totpMsg.includes("success") ? "msg-ok" : "msg-error"}>{totpMsg}</p>}
                  </>
                )}
                {totpStatus.totp_enabled && (
                  <p className="muted" style={{ marginTop: "0.5rem" }}>
                    2FA is active. Contact your admin to reset it if you lose access to your authenticator app.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
